// src/pages/LandingPage.jsx
import { Helmet } from 'react-helmet-async';
import Navbar from "../components/Navbar";
import Hero from "../components/Hero";
import Services from "../components/Services";
import Tech from "../components/Tech";
import FAQ from "../components/FAQ";
import CTA from "../components/CTA";
import Footer from "../components/Footer";

// ── JSON-LD: Organization Schema ──────────────────────────────
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Tracklio Hub",
  "alternateName": ["Tracklio", "TracklioHub", "Trackliohub", "Tracklib", "Tracklib Hub"],
  "url": "https://trackliohub.com",
  "logo": "https://trackliohub.com/logo192.png",
  "description": "Tracklio Hub is a leading custom software development company in Delhi, Bihar and Patna India offering ERP, banking software, inventory management, MDM, CRM and HRMS solutions.",
  "address": [
    {
      "@type": "PostalAddress",
      "addressLocality": "Delhi",
      "addressRegion": "Delhi",
      "addressCountry": "IN"
    },
    {
      "@type": "PostalAddress",
      "addressLocality": "Patna",
      "addressRegion": "Bihar",
      "addressCountry": "IN"
    }
  ],
  "areaServed": [
    "Delhi", "New Delhi", "Bihar", "Patna", "Mumbai", "Bangalore",
    "Hyderabad", "Chennai", "Kolkata", "Pune", "Noida", "Gurgaon",
    "Lucknow", "Jaipur", "India"
  ],
  "knowsAbout": [
    "ERP Software", "Banking Software", "Inventory Management",
    "Mobile Device Management", "CRM Software", "HRMS Software",
    "Custom Software Development", "Web Development", "Mobile App Development"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer support",
    "areaServed": ["IN"],
    "availableLanguage": ["English", "Hindi"]
  },
  "sameAs": [
    "https://www.linkedin.com/company/trackliohub",
    "https://twitter.com/trackliohub"
  ]
};

// ── JSON-LD: SoftwareApplication Schema ───────────────────────
const softwareSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Tracklio Hub Platform",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web Browser, Android, iOS",
  "description": "All-in-one business management platform with ERP, CRM, HRMS, Inventory, MDM and Project Management modules for businesses in Delhi, Bihar, Patna and across India.",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "INR",
    "description": "Free demo available"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Tracklio Hub",
    "url": "https://trackliohub.com"
  }
};

// ── JSON-LD: FAQ Schema (kept in sync with the visible <FAQ /> component) ──
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Which is the best software company in Delhi?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tracklio Hub is one of the best software development companies in Delhi offering ERP, CRM, HRMS, MDM and custom software solutions for businesses."
      }
    },
    {
      "@type": "Question",
      "name": "Which is the best software company in Patna Bihar?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tracklio Hub is a top software company in Patna, Bihar providing ERP software, inventory management, banking software and custom app development for businesses in Bihar."
      }
    },
    {
      "@type": "Question",
      "name": "What is Tracklio Hub?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tracklio Hub is an all-in-one business management platform offering ERP, CRM, HRMS, inventory management, MDM and custom software development services across India."
      }
    },
    {
      "@type": "Question",
      "name": "Does Tracklio Hub offer ERP software in India?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Tracklio Hub provides fully customizable ERP software for businesses in Delhi, Patna, Bihar and across India covering inventory, sales, HR, CRM and finance modules."
      }
    },
    {
      "@type": "Question",
      "name": "Does Tracklio Hub provide Mobile Device Management (MDM)?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Tracklio Hub provides a complete MDM solution to manage and secure Android and iOS devices for businesses and enterprises across India."
      }
    },
    {
      "@type": "Question",
      "name": "Does Tracklio Hub offer custom software development in Delhi?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Tracklio Hub is a custom software development company in Delhi. We build web apps, mobile apps, banking software, ERP and enterprise solutions."
      }
    },
    {
      "@type": "Question",
      "name": "Can I get a free demo of Tracklio Hub?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Tracklio Hub offers a free demo. Visit trackliohub.com or contact our team to schedule a demo at your convenience."
      }
    },
    {
      "@type": "Question",
      "name": "Does Tracklio Hub provide banking software in India?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Tracklio Hub develops custom banking software, cooperative banking solutions and financial management systems for businesses and institutions in India."
      }
    }
  ]
};

// ── JSON-LD: WebSite Schema ───────────────────────────────────
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Tracklio Hub",
  "url": "https://trackliohub.com",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://trackliohub.com/?s={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

// ── JSON-LD: BreadcrumbList Schema (homepage) ─────────────────
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://trackliohub.com/"
    }
  ]
};

// ── JSON-LD: LocalBusiness Schema (Delhi) ─────────────────────
// NOTE: replace the placeholder telephone number below with your real
// business number in E.164-style format (e.g. "+91-9876543210") —
// Google may reject/ignore LocalBusiness data with an invalid phone.
const localBusinessDelhi = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Tracklio Hub - Delhi",
  "image": "https://trackliohub.com/logo192.png",
  "url": "https://trackliohub.com",
  "description": "Custom software development company in Delhi offering ERP, CRM, HRMS, MDM, banking software and mobile app development.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Delhi",
    "addressRegion": "Delhi",
    "postalCode": "110001",
    "addressCountry": "IN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 28.6139,
    "longitude": 77.2090
  },
  "openingHours": "Mo-Fr 09:00-18:00",
  "priceRange": "₹₹",
  "telephone": "+91-XXXXXXXXXX",
  "email": "contact@trackliohub.com"
};

// ── JSON-LD: LocalBusiness Schema (Patna Bihar) ───────────────
const localBusinessPatna = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Tracklio Hub - Patna Bihar",
  "image": "https://trackliohub.com/logo192.png",
  "url": "https://trackliohub.com",
  "description": "Best software company in Patna Bihar offering ERP, inventory management, banking software, CRM and custom app development services.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Patna",
    "addressRegion": "Bihar",
    "postalCode": "800001",
    "addressCountry": "IN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 25.5941,
    "longitude": 85.1376
  },
  "openingHours": "Mo-Fr 09:00-18:00",
  "priceRange": "₹₹",
  "telephone": "+91-XXXXXXXXXX",
  "email": "contact@trackliohub.com"
};

// ─────────────────────────────────────────────────────────────
const LandingPage = () => {
  return (
    <>
      <Helmet>

        {/* ══ PRIMARY SEO ══════════════════════════════════════ */}
        <title>Tracklio Hub | Best Software Company in Delhi, Patna Bihar | ERP, CRM, MDM & Custom Software Development India</title>

        <meta name="description"
          content="Tracklio Hub — Best software development company in Delhi, Patna and Bihar India. We provide ERP software, banking software, inventory management, MDM, CRM, HRMS and custom web & mobile app development for businesses. Free demo available." />

        <meta name="keywords"
          content="
            software company in Delhi,
            software company in Patna,
            software company in Bihar,
            best software company Delhi,
            best software company Patna Bihar,
            software company near me,
            best software company near me,
            IT company near me,
            software development company Delhi,
            software development company Patna,
            IT company in Delhi,
            IT company in Bihar,
            IT company in Patna,
            custom software development Delhi,
            custom software development Patna,
            custom software development Bihar,
            custom software development company near me,
            top software company India,
            top IT company India,
            ERP,
            App,
            Software,
            ERP software Delhi,
            ERP software Patna,
            ERP software Bihar,
            ERP software India,
            ERP software company near me,
            banking software India,
            banking software Delhi,
            cooperative banking software India,
            inventory management system India,
            inventory management software Delhi,
            MDM solution India,
            mobile device management software,
            Android MDM India,
            iOS MDM India,
            DPC software India,
            CRM software India,
            CRM software Delhi,
            CRM software company near me,
            HRMS software India,
            HR software Delhi,
            payroll software India,
            project management software India,
            web development company Delhi,
            web development company Patna,
            web development company near me,
            mobile app development Delhi,
            mobile app development Bihar,
            mobile app development company near me,
            React developer India,
            Node.js developer Delhi,
            business management software India,
            enterprise software India,
            SaaS company India,
            startup software company Delhi,
            school management software India,
            hospital management software India,
            software company Noida,
            software company Gurgaon,
            software company Mumbai,
            software company Bangalore,
            software company Hyderabad,
            software company Chennai,
            software company Kolkata,
            software company Pune,
            software company Lucknow,
            software company Jaipur,
            Tracklio Hub,
            Tracklio,
            TracklioHub,
            trackliohub,
            tracklihub,
            Tracklib,
            Tracklib Hub,
            tracklio software
          " />

        <meta name="robots"  content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author"  content="Tracklio Hub" />
        <link rel="canonical" href="https://trackliohub.com/" />

        {/* ══ GEO / LOCAL SEO ══════════════════════════════════ */}
        {/* Primary registered/head office location — only one geo.region is standard */}
        <meta name="geo.region"    content="IN-DL" />
        <meta name="geo.placename" content="Delhi, India" />
        <meta name="geo.position"  content="28.6139;77.2090" />
        <meta name="ICBM"          content="28.6139, 77.2090" />
        <meta name="language"      content="English" />

        {/* ══ OPEN GRAPH ═══════════════════════════════════════ */}
        <meta property="og:type"         content="website" />
        <meta property="og:site_name"    content="Tracklio Hub" />
        <meta property="og:title"        content="Tracklio Hub | Best Software Company in Delhi, Patna Bihar | ERP, CRM, MDM India" />
        <meta property="og:description"  content="Best software development company in Delhi, Patna and Bihar. ERP, banking, inventory, MDM, CRM & HRMS solutions. Custom web & mobile app development across India." />
        <meta property="og:url"          content="https://trackliohub.com/" />
        <meta property="og:image"        content="https://trackliohub.com/og-home.jpg" />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt"    content="Tracklio Hub - Best Software Company Delhi Bihar India" />
        <meta property="og:locale"       content="en_IN" />

        {/* ══ TWITTER CARD ════════════════════════════════════ */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@trackliohub" />
        <meta name="twitter:title"       content="Tracklio Hub | Best Software Company Delhi, Patna Bihar India" />
        <meta name="twitter:description" content="ERP, banking, inventory, MDM, CRM & HRMS solutions. Custom web & mobile app development in Delhi, Bihar and across India." />
        <meta name="twitter:image"       content="https://trackliohub.com/og-home.jpg" />

        {/* ══ JSON-LD SCHEMAS ══════════════════════════════════ */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessDelhi)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessPatna)}</script>

      </Helmet>

      {/* ── Page Components ── */}
      <Navbar />
      <main>
        <Hero />
        <Services />
        <Tech />
        <FAQ />
        <CTA />
      </main>
      <Footer />
    </>
  );
};

export default LandingPage;